import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Search, Megaphone, Loader2, Bell } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const TAGS = ["All", "General", "Maintenance", "Events"];
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notices, setNotices] = useState(null);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("All");

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api
      .get("/notices")
      .then((r) => setNotices(r.data))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setNotices([]);
      });
  }, [user]);

  const filtered = useMemo(() => {
    if (!notices) return [];
    return notices.filter((n) => {
      const tagOk = tag === "All" || n.tag === tag;
      const q = query.trim().toLowerCase();
      const qOk = !q || n.title.toLowerCase().includes(q) || n.body.toLowerCase().includes(q);
      return tagOk && qOk;
    });
  }, [notices, query, tag]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="dashboard-loading">
        <Loader2 className="animate-spin text-blue-600" size={28} />
      </div>
    );
  }

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="dashboard-home-link"><Logo size={36} /></Link>
          <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block" data-testid="dashboard-user-info">
              <p className="text-sm font-bold text-slate-900">{user.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-blue-600">
                {user.role === "admin" ? "Platform Admin" : `Resident${user.unit ? ` · ${user.unit}` : ""}`}
              </p>
            </div>
            <button
              onClick={doLogout}
              data-testid="dashboard-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-red-200 hover:text-red-600"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="eyebrow mb-2">Resident Dashboard</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="dashboard-heading">
            Good to see you, {user.name.split(" ")[0]}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Every announcement from your society office, in one place.</p>
        </motion.div>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center" data-testid="dashboard-notices-panel">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="notices-search-input"
              className="input-field pl-11"
              placeholder="Search notices…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {TAGS.map((t) => (
              <button
                key={t}
                onClick={() => setTag(t)}
                data-testid={`notices-filter-${t.toLowerCase()}`}
                className={`rounded-full border px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                  tag === t ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:text-slate-800"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {notices === null ? (
            <div className="flex items-center justify-center gap-3 p-16 text-slate-400" data-testid="notices-loading">
              <Loader2 size={20} className="animate-spin" /> Loading notices…
            </div>
          ) : filtered.length === 0 ? (
            <p className="card-soft p-12 text-center font-mono text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="notices-empty">
              No notices match your search
            </p>
          ) : (
            filtered.map((n) => (
              <motion.article
                key={n.id}
                data-testid={`dashboard-notice-${n.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="card-soft p-6 transition-all duration-300 hover:border-blue-200 hover:shadow-md sm:p-7"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-blue-50 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700">
                    {n.tag}
                  </span>
                  {n.urgent && (
                    <span className="rounded-full bg-red-50 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-red-600">
                      Urgent
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    <Bell size={11} /> {formatDate(n.created_at)}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">{n.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{n.body}</p>
                <p className="mt-4 border-t border-slate-100 pt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Posted by {n.author}
                </p>
              </motion.article>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
